import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TimelineHistoricoComponent, TimelineHistoricoItem } from '@shared/components/timeline-historico/timeline-historico.component';
import { CardHistoryItem, HistoryCompraLink, avatarColorFromSeed, formatCodigoCompra } from '../card-edit-dialog.types';

/**
 * Histórico do card. Um único componente renderizado em dois pontos do dialog:
 * - modo "timeline": `app-timeline-historico` (ocorrências e comentários, com filtros) na coluna esquerda;
 * - modo "detalhe": coluna direita com o diff, a justificativa, o aviso de aprovação e as compras citadas
 *   da ocorrência aberta em "Detalhes".
 * O estado (lista + item selecionado) vive no shell: a timeline é destruída
 * quando um painel (Anexos/Comentários) abre, mas a coluna de detalhe precisa
 * persistir — e a coluna direita é filha direta do flex do corpo do dialog.
 * "Nova anotação" grava uma ocorrência tipo 5 pelo shell; comentário é conversa do card e não entra no histórico.
 */
@Component({
  selector: 'app-ced-historico',
  standalone: true,
  imports: [
    DatePipe,
    MatIconModule,
    MatTooltipModule,
    LoadingBoxComponent,
    TimelineHistoricoComponent,
  ],
  templateUrl: './ced-historico.component.html',
  styleUrls: ['../ced-scrollbars.css', './ced-historico.component.css'],
})
export class CedHistoricoComponent {
  /** Ponto de renderização: timeline (coluna esquerda) ou detalhe (coluna direita). */
  @Input() modo: 'timeline' | 'detalhe' = 'timeline';
  /** Histórico montado pelo shell (dono do fetch/recarga do histórico da compra). */
  @Input() history: CardHistoryItem[] = [];
  /** Primeira carga do histórico em andamento. */
  @Input() carregando = false;
  /** Item selecionado para o painel de detalhe (estado vive no shell). */
  @Input() selectedItem: CardHistoryItem | null = null;
  /** Usuário com permissão de alterar a compra: libera "Nova anotação". */
  @Input() permiteAnotacao = false;
  /** Liga o spinner do "Registrar" enquanto o shell grava. */
  @Input() registrandoAnotacao = false;

  @ViewChild(TimelineHistoricoComponent) private timeline?: TimelineHistoricoComponent;

  /** Pedido de abertura do detalhe de um item (o shell guarda a seleção). */
  @Output() detalhar = new EventEmitter<CardHistoryItem>();
  /** Pedido de fechamento da coluna de detalhe. */
  @Output() fechar = new EventEmitter<void>();
  /** Pedido de abertura de outra compra citada pela ocorrência (cotação de origem / pedido gerado). */
  @Output() abrirCompra = new EventEmitter<HistoryCompraLink>();
  /** Anotação digitada em "Nova anotação"; quem grava é o shell. */
  @Output() registrarAnotacao = new EventEmitter<{ descricao: string; dataOcorrencia: Date }>();

  /** O shell chama depois de gravar a anotação com sucesso. */
  limparAnotacao(): void {
    this.timeline?.limparAnotacao();
  }

  /** A timeline devolve o próprio objeto recebido em `history`. */
  onDetalhar(item: TimelineHistoricoItem): void {
    this.detalhar.emit(item as CardHistoryItem);
  }

  /** Identificador de exibição da compra citada ("#1030"). */
  codigoCompra(compra: HistoryCompraLink): string {
    return formatCodigoCompra(compra.codigo);
  }

  /** Resumo do aviso de aprovação ("3 pessoas notificadas"). */
  resumoNotificados(quantidade: number): string {
    return quantidade === 1 ? '1 pessoa notificada' : `${quantidade} pessoas notificadas`;
  }

  corAvatar(nome: string): string {
    return avatarColorFromSeed(nome);
  }

  iniciais(nome: string): string {
    const partes = (nome || '').trim().split(/\s+/).filter(Boolean);
    if (!partes.length) return '?';
    const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
    return (partes[0][0] + ultima).toUpperCase();
  }
}
